<script>
import * as m from '@app/paraglide/messages.js';
import { styleState, selectPreset, initializePresets, savePreset as savePresetToStore, deletePreset as deletePresetFromStore } from "@app/store/styleStore.svelte.js";
import { fontState } from "@app/store/fontStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";

function savePreset() {
    Swal.fire({
        ...SWAL_BASE,
        titleText: m.styles_save_preset_title(),
        text: m.styles_save_preset_prompt(),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.styles_save_preset_confirm(),
        denyButtonText: m.styles_cancel_button(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        inputLabel: m.styles_preset_name_label(),
        inputValue: styleState.selectedPreset?.name || "Untitled",
        input: "text",
        customClass: SWAL_CLASSES.denyCancel,
    })
        .then(async (result) => {
            if (result.isConfirmed) {
                const presetName = result.value;
                if (presetName) {
                    try {
                        await savePresetToStore(presetName, uuidv4);
                        toast.push(m.styles_preset_saved_toast(), {
                            classes: ["toast-success"],
                        });
                    } catch (error) {
                        console.error("Error saving preset:", error);
                        toast.push(error.message, {
                            classes: ["toast-danger"],
                        });
                    }
                }
            } else {
                toast.push(m.styles_save_aborted_toast(), { classes: ["toast-info"] });
            }
        })
        .catch((err) => {
            console.error(err);
            toast.push(err.message, { classes: ["toast-danger"] });
        });
}

function deletePreset() {
    if (!styleState.selectedPreset) {
        toast.push(m.styles_no_preset_selected_toast(), { classes: ["toast-warning"] });
        return;
    }

    Swal.fire({
        ...SWAL_BASE,
        titleText: m.styles_delete_preset_title(),
        text: m.styles_delete_preset_confirm_text({presetName: styleState.selectedPreset.name}),
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: m.styles_delete_button(),
        denyButtonText: m.styles_cancel_button(),
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: SWAL_CLASSES.destructiveDeny,
    })
        .then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await deletePresetFromStore();
                    toast.push(m.styles_preset_deleted_toast(), {
                        classes: ["toast-success"],
                    });
                } catch (error) {
                    console.error("Error deleting preset:", error);
                    toast.push(m.styles_delete_failed_toast({error: error.message}), {
                        classes: ["toast-danger"],
                    });
                }
            }
        })
        .catch((err) => {
            console.error(err);
            toast.push(err.message, { classes: ["toast-danger"] });
        });
}
</script>

<div aria-label={m.styles_controls_aria_label()} role="region">
    <form aria-label={m.styles_form_aria_label()}>
        {#if !styleState.ccMode}
            <div class="row g-2 mb-2 border-bottom border-secondary">
                <div class="col-2">
                    <select
                        id="displayProfileSelect"
                        class="form-select form-control-sm"
                        bind:value={styleState.borderStyle}
                        aria-label={m.styles_display_aria_label()}>
                        <option value="3">{m.styles_solid_background_option()}</option>
                        <option value="1">{m.styles_text_outline_option()}</option>
                    </select>
                    <label class="form-label small" for="displayProfileSelect"
                        >{m.styles_display_label()}</label>
                </div>
                <div class="col-2">
                    <select
                        id="fontFamilySelect"
                        class="form-select form-control-sm"
                        style="font-family: {styleState.fontFamily};"
                        bind:value={styleState.fontFamily}
                        aria-label={m.styles_font_family_aria_label()}>
                        <option style="font-family: Arial;">Arial</option>
                        <option style="font-family: Verdana;">Verdana</option>
                        <option style="font-family: Helvetica;"
                            >Helvetica</option>
                        <option style="font-family: Tahoma;">Tahoma</option>
                        <option style="font-family: Trebuchet MS;"
                            >Trebuchet MS</option>
                        <option style="font-family: Times New Roman;"
                            >Times New Roman</option>
                        <option style="font-family: Georgia;">Georgia</option>
                        <option style="font-family: Garamond;">Garamond</option>
                        <option style="font-family: Courier New;"
                            >Courier New</option>
                        <option style="font-family: Brush Script MT;"
                            >Brush Script MT</option>
                        <option style="font-family: serif;">serif</option>
                        <option style="font-family: sans-serif;"
                            >sans-serif</option>
                        <option style="font-family: plexMono;">plexMono</option>
                        <option style="font-family: cursive;">cursive</option>
                        <option style="font-family: fantasy;">fantasy</option>
                        {#each fontState as font (font.id)}
                            <option style="font-family: {font.name};"
                                >{font.name}</option>
                        {/each}
                    </select>
                    <label class="form-label small" for="fontFamilySelect"
                        >{m.styles_font_family_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="fontSizeInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        step="1"
                        bind:value={styleState.fontSize}
                        aria-label={m.styles_font_size_aria_label()}
                        aria-describedby="fontSizeLabel" />
                    <label
                        id="fontSizeLabel"
                        class="form-label small"
                        for="fontSizeInput">{m.styles_font_size_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="fontColorInput"
                        type="color"
                        class="form-control form-control-sm"
                        bind:value={styleState.color}
                        aria-label={m.styles_font_color_aria_label()} />
                    <label class="form-label small" for="fontColorInput"
                        >{m.styles_primary_label()}</label>
                </div>
                <div class="col">
                    <input
                        id="textopacityInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        step="1"
                        max="100"
                        bind:value={styleState.opacity}
                        aria-label={m.styles_text_opacity_aria_label()} />
                    <label class="form-label small" for="textopacityInput"
                        >{m.styles_opacity_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="letterSpacingInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        step="1"
                        bind:value={styleState.spacing}
                        aria-label={m.styles_letter_spacing_aria_label()} />
                    <label class="form-label small" for="letterSpacingInput"
                        >{m.styles_spacing_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="secondaryColorInput"
                        type="color"
                        class="form-control form-control-sm"
                        bind:value={styleState.outlineColor}
                        aria-label={m.styles_secondary_color_aria_label()} />
                    <label class="form-label small" for="secondaryColorInput"
                        >{m.styles_secondary_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="secondaryOpacityInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        step="1"
                        max="100"
                        bind:value={styleState.outlineOpacity}
                        aria-label={m.styles_secondary_opacity_aria_label()} />
                    <label class="form-label small" for="secondaryOpacityInput"
                        >{m.styles_opacity_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="paddingInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        step="0.1"
                        bind:value={styleState.outline}
                        aria-label={m.styles_padding_aria_label()} />
                    <label class="form-label small" for="paddingInput"
                        >{m.styles_outline_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="shadowColorInput"
                        type="color"
                        class="form-control form-control-sm"
                        bind:value={styleState.shadowColor}
                        aria-label={m.styles_shadow_color_aria_label()} />
                    <label class="form-label small" for="shadowColorInput"
                        >{m.styles_shadow_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="shadowOpacityInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="1"
                        step="1"
                        max="100"
                        bind:value={styleState.shadowOpacity}
                        aria-label={m.styles_shadow_opacity_aria_label()} />
                    <label class="form-label small" for="shadowOpacityInput"
                        >{m.styles_opacity_label()}</label>
                </div>
                <div class="col-2">
                    <input
                        id="shadowSizeInput"
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        step="0.1"
                        bind:value={styleState.shadow}
                        aria-label={m.styles_shadow_size_aria_label()} />
                    <label class="form-label small" for="shadowSizeInput"
                        >{m.styles_shadow_size_label()}</label>
                </div>                
            </div>
        {:else}
            <p class="small text-muted text-center mt-4">{m.styles_cc_mode_enabled()}</p>
            <hr/>
        {/if}
        <div class="row align-items-center g-1">
            {#if !styleState.ccMode}
                <div class="col-6">
                    <select
                        id="presetSelect"
                        class="form-select form-select-sm"
                        bind:value={styleState.selectedPreset}
                        onchange={() => selectPreset()}
                        aria-label={m.styles_preset_select_aria_label()}>
                        {#each styleState.presets as preset (preset.id)}
                            <option value={preset}>{preset.name}</option>
                        {/each}
                    </select>
                </div>
                <div class="col-auto">
                    <div
                        class="btn-group btn-group-sm"
                        role="group"
                        aria-label={m.styles_preset_actions_aria_label()}>
                        <button
                            title={m.styles_save_preset_button_title()}
                            type="button"
                            id="savePresetBtn"
                            class="btn btn-outline-dark"
                            aria-label={m.styles_save_preset_button_aria_label()}
                            onclick={() => savePreset()}>
                            <i class="bi bi-upload" aria-hidden="true"></i>
                        </button>
                        <button
                            title={m.styles_remove_preset_button_title()}
                            type="button"
                            id="deletePresetBtn"
                            class="btn btn-outline-dark"
                            aria-label={m.styles_delete_preset_button_aria_label()}
                            onclick={() => deletePreset()}>
                            <i class="bi bi-trash" aria-hidden="true"></i>
                        </button>
                        <button
                            title={m.styles_refresh_presets_button_title()}
                            type="button"
                            id="refreshPresetsBtn"
                            class="btn btn-outline-dark"
                            aria-label={m.styles_refresh_presets_button_aria_label()}
                            onclick={() => initializePresets()}>
                            <i class="bi bi-arrow-clockwise"></i>
                        </button>
                    </div>
                </div>
            {/if}
            <div class="col">
                <div class="form-check form-switch float-end">
                    <input
                        title={m.styles_cc_mode_title()}
                        class="form-check-input"
                        type="checkbox"
                        role="switch"
                        id="ccModeSwitch"
                        bind:checked={styleState.ccMode}
                        aria-labelledby="ccModeSwitchLabel" />
                    <label class="form-check-label" for="ccModeSwitch"
                        ><i class="bi bi-badge-cc"></i></label>
                </div>
            </div>
        </div>
    </form>
</div>
